import { useState } from 'react';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { getImagePath } from '@/utils/images';
import { useAppContext } from '@/components/utilities/AppContext';

const TAG_COLORS = {
  online: 'bg-brand-400',
  offline: 'bg-[#9f5cf0]'
};

function SpeakerInfo({ name, title, avatar }) {
  return (
    <div className="flex items-center gap-3">
      {avatar?.url && (
        <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full">
          <Image src={getImagePath(avatar)} alt={name || ''} fill className="object-cover" />
        </div>
      )}
      <div>
        <div className="txt-lg font-medium text-black">{name}</div>
        <div className="txt-md text-grey-700">{title}</div>
      </div>
    </div>
  );
}

function Speakers({ webinar }) {
  const hasSecondSpeaker = Boolean(
    webinar.secondSpeakerName || webinar.secondSpeakerTitle || webinar.secondSpeakerAvatar?.url
  );

  return (
    <div className="flex flex-col gap-4">
      <SpeakerInfo name={webinar.speakerName} title={webinar.speakerTitle} avatar={webinar.speakerAvatar} />
      {hasSecondSpeaker && (
        <SpeakerInfo
          name={webinar.secondSpeakerName}
          title={webinar.secondSpeakerTitle}
          avatar={webinar.secondSpeakerAvatar}
        />
      )}
    </div>
  );
}

function TagBadge({ tag }) {
  if (!tag) return null;

  return (
    <span
      className={`inline-flex w-fit items-center rounded-md p-1.5 text-sm font-semibold tracking-tight text-white uppercase ${TAG_COLORS[tag] || 'bg-brand-400'}`}
    >
      {tag}
    </span>
  );
}

function UpcomingWebinarCard({ webinar, labels }) {
  const { locale } = useAppContext();
  const ctaLabel = webinar.ctaLabel || labels.ctaLabel;
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState('idle');
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '' });
  const [honeypot, setHoneypot] = useState('');

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open) {
      setFormState('idle');
      setFormData({ firstName: '', lastName: '', email: '' });
      setHoneypot('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState('loading');

    try {
      const shouldSubscribe = webinar.subscribe ?? true;
      const subscribeLists = webinar.subscribeLists || 'newsletters';

      const identifyData = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName
      };
      if (shouldSubscribe) {
        identifyData.subscribe = subscribeLists
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }

      const tagData = {
        name: 'webinar',
        data: {
          id: webinar.tagName || webinar.id,
          date: {
            datatype: 'datetime',
            value: new Date().toISOString()
          }
        }
      };

      // The registration (profile, interest lists, tag) is written to Leadhub from
      // the backend via the insights batch feeder — see /api/webinar-registration.
      const res = await fetch('/api/webinar-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identify: identifyData, tag: tagData, hp: honeypot, locale })
      });
      if (!res.ok) throw new Error('submit-failed');

      // Browser-side identify stays so Leadhub can couple this device's cookie
      // (client_id) with the profile — the backend has no access to it.
      if (window.lhi) {
        window.lhi('identify', identifyData);
      }

      setFormState('success');
    } catch {
      setFormState('error');
    }
  };

  const formattedDate = webinar.date
    ? new Date(webinar.date).toLocaleString(locale !== 'pl' ? 'cs-CZ' : 'pl-PL', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'Europe/Prague'
      })
    : '';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {/* Card */}
      <div className="flex h-full flex-col justify-between rounded-2xl bg-white p-4 pb-6 shadow-[0px_11px_24px_0px_rgba(0,0,0,0.05),0px_43px_43px_0px_rgba(0,0,0,0.06)]">
        <div className="flex flex-1 flex-col gap-6">
          <Speakers webinar={webinar} />
          <TagBadge tag={webinar.tag} />
          <h3 className="heading-6 text-black">{webinar.name}</h3>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button onClick={() => handleOpenChange(true)}>{ctaLabel}</Button>
          <DateDisplay date={formattedDate} />
        </div>
      </div>

      {/* Modal via portal */}
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{webinar.name}</DialogTitle>
        <DialogDescription className="sr-only">{labels.dialogDescription}</DialogDescription>

        <div className="flex flex-col gap-6">
          <Speakers webinar={webinar} />
          <TagBadge tag={webinar.tag} />
          <h3 className="heading-6 text-black">{webinar.name}</h3>

          {webinar.description && (
            <div className="txt-md text-grey-700 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5">
              <Markdown
                rehypePlugins={[rehypeRaw]}
                components={{
                  a: ({ href, children }) => {
                    const isExternal = href && !href.startsWith('/') && !href.startsWith('#');
                    return (
                      <a href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                        {children}
                      </a>
                    );
                  }
                }}
              >
                {webinar.description}
              </Markdown>
            </div>
          )}

          {formState === 'success' ? (
            <div className="rounded-lg bg-green-50 p-4 text-center text-green-700">
              <p className="font-medium">{labels.successMessage}</p>
              <p className="mt-2 text-sm">{labels.successNote}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                name="company"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                className="absolute h-0 w-0 overflow-hidden opacity-0"
              />
              <Input
                name="given-name"
                autoComplete="given-name"
                placeholder={labels.placeholderFirstName}
                value={formData.firstName}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                required
                className="border-brand-400 focus-visible:border-brand-400"
              />
              <Input
                name="family-name"
                autoComplete="family-name"
                placeholder={labels.placeholderLastName}
                value={formData.lastName}
                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                required
                className="border-brand-400 focus-visible:border-brand-400"
              />
              <Input
                type="email"
                name="email"
                autoComplete="email"
                placeholder={labels.placeholderEmail}
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                required
                className="border-brand-400 focus-visible:border-brand-400"
              />

              {formState === 'error' && <div className="txt-sm text-red-600">{labels.errorMessage}</div>}

              <div className="flex items-center justify-between pt-2">
                <Button type="submit" disabled={formState === 'loading'}>
                  {formState === 'loading' ? labels.loadingMessage : ctaLabel}
                </Button>
                <DateDisplay date={formattedDate} />
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DateDisplay({ date }) {
  if (!date) return null;

  return (
    <div className="flex items-center gap-2">
      <Icon type="calendar" width={24} height={24} color="black" />
      <span className="txt-md font-semibold tracking-tight text-black uppercase">{date}</span>
    </div>
  );
}

const DEFAULTS = {
  cs: {
    ctaLabel: 'Přihlásit se na webinář',
    dialogDescription: 'Registrace na webinář',
    placeholderFirstName: 'Vaše jméno',
    placeholderLastName: 'Vaše příjmení',
    placeholderEmail: 'Vaše e-mailová adresa',
    successMessage: 'Registrace proběhla úspěšně!',
    successNote: 'Nyní prosím potvrďte e-mail, který jsme vám zaslali. Poté vám dorazí odkaz na webinář.',
    errorMessage: 'Něco se pokazilo. Zkuste to znovu.',
    loadingMessage: 'Odesílám...'
  },
  pl: {
    ctaLabel: 'Zapisz się na webinar',
    dialogDescription: 'Rejestracja na webinar',
    placeholderFirstName: 'Twoje imię',
    placeholderLastName: 'Twoje nazwisko',
    placeholderEmail: 'Twój adres e-mail',
    successMessage: 'Rejestracja przebiegła pomyślnie!',
    successNote: 'Teraz potwierdź e-mail, który do Ciebie wysłaliśmy. Następnie otrzymasz link do webinaru.',
    errorMessage: 'Coś poszło nie tak. Spróbuj ponownie.',
    loadingMessage: 'Wysyłanie...'
  }
};

const UpcomingWebinarsBlock = ({
  hidden,
  upcomingWebinarsTitle,
  upcomingWebinars,
  placeholderFirstName,
  placeholderLastName,
  placeholderEmail,
  successMessage,
  successNote,
  errorMessage,
  loadingMessage
}) => {
  const { locale } = useAppContext();
  const webinars = upcomingWebinars || [];

  if (hidden || webinars.length === 0) return null;

  const d = locale !== 'pl' ? DEFAULTS.cs : DEFAULTS.pl;
  const labels = {
    ctaLabel: d.ctaLabel,
    dialogDescription: d.dialogDescription,
    placeholderFirstName: placeholderFirstName || d.placeholderFirstName,
    placeholderLastName: placeholderLastName || d.placeholderLastName,
    placeholderEmail: placeholderEmail || d.placeholderEmail,
    successMessage: successMessage || d.successMessage,
    successNote: successNote || d.successNote,
    errorMessage: errorMessage || d.errorMessage,
    loadingMessage: loadingMessage || d.loadingMessage
  };

  return (
    <div className="mx-auto mt-10 mb-20 w-[90vw] xl:mb-32 xl:w-(--container-width)">
      {upcomingWebinarsTitle && (
        <VisibleLoading delay={100}>
          {({ ref, className }) => (
            <h2 ref={ref} className={`mb-12 text-center ${className || ''}`.trim()}>
              {upcomingWebinarsTitle}
            </h2>
          )}
        </VisibleLoading>
      )}

      <div className="-mx-3 flex flex-wrap justify-center gap-y-6">
        {webinars.map((webinar, index) => (
          <VisibleLoading key={webinar.id} delay={(index + 1) * 200}>
            {({ ref, className }) => (
              <div ref={ref} className={`basis-full px-3 xl:basis-1/3 ${className || ''}`.trim()}>
                <UpcomingWebinarCard webinar={webinar} labels={labels} />
              </div>
            )}
          </VisibleLoading>
        ))}
      </div>
    </div>
  );
};

export default UpcomingWebinarsBlock;
